import cn from 'classnames';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';
import { useOnMount } from 'src/hooks/useOnMount';

import { FileData, ImageData } from 'src/types/components';

import Play from 'assets/icons/play.svg';

import Cta from 'components/BaseHelpers/Cta';
import Image from 'components/BaseHelpers/Image';

import Text from '../Text';

import styles from './VideoPlayer.module.scss';

export type VideoPlayerProps = {
  thumbnailImage?: ImageData;
  platform: 'youtube' | 'vimeo' | 'file';
  videoData: string;
  videoFile?: FileData;
  className?: string;
  onPlay?: () => void;
};

export type VideoPlayerHandle = {
  pause: () => void;
};

type VideoOverlayProps = {
  image: ImageData;
  onClick: () => void;
};

type VideoMessageData = {
  event?: string;
  info?: unknown;
};

const parseVideoMessageData = (data: MessageEvent['data']): VideoMessageData | null => {
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as VideoMessageData;
    } catch {
      return null;
    }
  }

  if (data && typeof data === 'object') {
    return data as VideoMessageData;
  }

  return null;
};

const isYoutubePlayMessage = (messageData: VideoMessageData) => {
  if (messageData.event === 'onStateChange') {
    return messageData.info === 1;
  }

  if (
    messageData.event === 'infoDelivery' &&
    messageData.info &&
    typeof messageData.info === 'object' &&
    'playerState' in messageData.info
  ) {
    return messageData.info.playerState === 1;
  }

  return false;
};

const isPlayMessage = (messageData: VideoMessageData, platform: VideoPlayerProps['platform']) => {
  if (platform === 'youtube') {
    return isYoutubePlayMessage(messageData);
  }

  return messageData.event === 'play';
};

const VideoOverlay = ({ image, onClick }: VideoOverlayProps) => {
  const dictionary = useDictionary();
  const [isVisible, setIsVisible] = useState(true);
  const handleClick = () => {
    onClick();
    setIsVisible(false);
  };

  return (
    <div
      className={cn(styles.overlay, {
        [styles['overlay--hidden']]: !isVisible,
      })}
      inert={!isVisible}
    >
      <Image {...image} className={styles.thumbnail} fill />
      <Cta className={styles.cta} tag="button" onClick={handleClick} theme="dark">
        <Text field={dictionary.playVideoLabel} />
        <Play className={styles['play-icon']} />
      </Cta>
    </div>
  );
};

const formatEmbedCode = (embedCode: string, platform: VideoPlayerProps['platform']): string => {
  // Ensure the YouTube embed code has the enablejsapi=1 parameter
  if (platform === 'youtube') {
    return `<iframe src="https://www.youtube.com/embed/${embedCode}?enablejsapi=1" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`;
  } else if (platform === 'vimeo') {
    return `<iframe src="https://player.vimeo.com/video/${embedCode}?badge=0&amp;autopause=0&amp;player_id=0" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerpolicy="strict-origin-when-cross-origin" style="position:absolute;top:0;left:0;width:100%;height:100%;" title="Video"></iframe></div><script src="https://player.vimeo.com/api/player.js"></script>`;
  } else {
    return '';
  }
};

const VideoPlayer = forwardRef<VideoPlayerHandle, VideoPlayerProps>((props, ref): JSX.Element => {
  const { thumbnailImage, className, videoData, videoFile, platform, onPlay } = props;
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const fallbackImage = { src: '/images/video-thumbnail-fallback.png', alt: '' };
  const imageData = thumbnailImage ?? fallbackImage;

  const getIframe = useCallback(() => {
    if (!iframeContainerRef.current) return null;

    return iframeContainerRef.current.querySelector('iframe');
  }, []);

  const postVideoCommand = useCallback(
    (command: 'play' | 'pause') => {
      if (platform === 'file') {
        if (command === 'play') {
          videoRef.current?.play();
        } else {
          videoRef.current?.pause();
        }
        return;
      }

      const iframe = getIframe();
      if (!iframe || !iframe.contentWindow) return;

      if (platform === 'youtube') {
        iframe.contentWindow.postMessage(
          JSON.stringify({
            event: 'command',
            func: command === 'play' ? 'playVideo' : 'pauseVideo',
            args: '',
          }),
          '*'
        );
      }

      if (platform === 'vimeo') {
        iframe.contentWindow.postMessage(JSON.stringify({ method: command }), '*');
      }
    },
    [getIframe, platform]
  );

  const subscribeToPlayerEvents = useCallback(() => {
    const iframe = getIframe();
    if (!iframe || !iframe.contentWindow) return;

    if (platform === 'youtube') {
      iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }),
        '*'
      );
    }

    if (platform === 'vimeo') {
      iframe.contentWindow.postMessage(
        JSON.stringify({ method: 'addEventListener', value: 'play' }),
        '*'
      );
    }
  }, [getIframe, platform]);

  useImperativeHandle(ref, () => ({
    pause: () => postVideoCommand('pause'),
  }));

  const handlePlayVideo = () => {
    onPlay?.();
    postVideoCommand('play');
  };

  // Detect client to prevent hydration error
  const { isMounted } = useOnMount();
  const embedCode = formatEmbedCode(videoData, platform);
  const videoFileUrl = platform === 'file' ? videoFile?.url : undefined;

  useEffect(() => {
    if (!isMounted) return;

    const iframe = getIframe();
    if (!iframe) return;

    subscribeToPlayerEvents();
    iframe.addEventListener('load', subscribeToPlayerEvents);

    const handleMessage = (event: MessageEvent) => {
      if (!iframe || event.source !== iframe.contentWindow) return;

      const messageData = parseVideoMessageData(event.data);

      if (messageData && isPlayMessage(messageData, platform)) {
        onPlay?.();
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      iframe.removeEventListener('load', subscribeToPlayerEvents);
      window.removeEventListener('message', handleMessage);
    };
  }, [getIframe, isMounted, onPlay, platform, subscribeToPlayerEvents]);

  if (platform === 'file' && !videoFileUrl) {
    return <></>;
  }

  if (platform !== 'file' && !embedCode) {
    return <></>;
  }

  return (
    <div className={cn(styles.main, className)}>
      {platform === 'file' && videoFileUrl && (
        <video
          ref={videoRef}
          className={styles.video}
          controls
          playsInline
          preload="metadata"
          src={videoFileUrl}
          onPlay={onPlay}
        />
      )}
      {platform !== 'file' && isMounted && (
        <div
          ref={iframeContainerRef}
          className={styles['iframe-container']}
          dangerouslySetInnerHTML={{ __html: embedCode }}
        />
      )}
      <VideoOverlay image={imageData} onClick={handlePlayVideo} />
    </div>
  );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
